import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiService, ApiEndpoints } from '../../services/ApiService';
import Alert from '@mui/material/Alert';

/**
 * Control interface for dropdown
 */
interface Control {
  id: number;
  name: string;
  description: string;
}

/**
 * Material Requirement Create/Edit Page
 * 
 * Form for creating new compliance requirements or editing existing ones
 */
interface MaterialRequirementCreatePageProps {
  editId?: string;
}

export default function MaterialRequirementCreatePage({ editId }: MaterialRequirementCreatePageProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const controlId = searchParams.get('controlId');
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [controls, setControls] = useState<Control[]>([]);
  const [loadingControls, setLoadingControls] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  
  const isEditMode = !!editId;
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    control_id: controlId || '',
    owner: '',
    priority: 'medium',
    status: 'planned'
  });

  /**
   * Load controls for dropdown
   */
  useEffect(() => {
    const fetchControls = async () => {
      try {
        setLoadingControls(true);
        const data = await ApiService.get(ApiEndpoints.CONTROLS.LIST) as Control[];
        setControls(data);
      } catch (err) {
        console.error('Error loading controls:', err);
        setError('Failed to load controls');
      } finally {
        setLoadingControls(false);
      }
    };

    fetchControls();
  }, []);

  /**
   * Load existing requirement data for editing
   */
  useEffect(() => {
    if (isEditMode && editId) {
      const fetchRequirementData = async () => {
        try {
          setLoadingData(true);
          const data = await ApiService.get(ApiEndpoints.REQUIREMENTS.DETAIL(editId)) as any;
          setFormData({
            title: data.title || '',
            description: data.description || '',
            control_id: data.control_id?.toString() || '',
            owner: data.owner || '',
            priority: data.priority || 'medium',
            status: data.status || 'planned'
          });
        } catch (err) {
          console.error('Error loading requirement data:', err);
          setError('Failed to load requirement data');
        } finally {
          setLoadingData(false);
        }
      };

      fetchRequirementData();
    }
  }, [isEditMode, editId]);

  /**
   * Handle form field changes
   */
  const handleChange = (field: string) => (event: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    
    if (!formData.control_id) {
      setError('Control is required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const requirementData = {
        ...formData,
        control_id: parseInt(formData.control_id)
      };

      if (isEditMode) {
        await ApiService.put(ApiEndpoints.REQUIREMENTS.UPDATE(editId!), requirementData);
      } else {
        await ApiService.post(ApiEndpoints.REQUIREMENTS.CREATE, requirementData);
      }

      // Navigate back to requirements list
      navigate('/requirements');
    } catch (err: any) {
      console.error('Error saving requirement:', err);
      setError(err.message || `Failed to ${isEditMode ? 'update' : 'create'} requirement`);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle cancel
   */
  const handleCancel = () => {
    navigate('/requirements');
  };

  if (loadingData) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {isEditMode ? 'Edit Requirement' : 'Create New Requirement'}
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            
            <TextField
              label="Requirement Title"
              value={formData.title}
              onChange={handleChange('title')}
              required
              fullWidth
              placeholder="Enter a descriptive title for this requirement"
            />

            <TextField
              label="Description"
              value={formData.description}
              onChange={handleChange('description')}
              multiline
              rows={4}
              fullWidth
              placeholder="Describe what this requirement specifies and how it should be implemented"
            />

            <FormControl fullWidth required>
              <InputLabel>Control</InputLabel>
              <Select
                value={formData.control_id}
                onChange={handleChange('control_id')}
                label="Control"
                disabled={loadingControls}
              >
                {controls.map((control) => (
                  <MenuItem key={control.id} value={control.id.toString()}>
                    {control.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Owner"
              value={formData.owner}
              onChange={handleChange('owner')}
              fullWidth
              placeholder="Who is responsible for implementing this requirement?"
            />

            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                onChange={handleChange('priority')}
                label="Priority"
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={handleChange('status')}
                label="Status"
              >
                <MenuItem value="planned">Planned</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="implemented">Implemented</MenuItem>
                <MenuItem value="under_review">Under Review</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button 
                variant="outlined" 
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                variant="contained" 
                disabled={loading || loadingControls}
              >
                {loading ? (
                  <>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    {isEditMode ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  isEditMode ? 'Update Requirement' : 'Create Requirement'
                )}
              </Button>
            </Box>
          </Box>
        </form>
      </Paper>
    </Box>
  );
}
